/* eslint-disable react/jsx-props-no-spreading */
import {
    FormControl,
    FormLabel,
    FormControlLabel,
    RadioGroup,
    FormHelperText,
    Radio,
    Stack,
} from "@mui/material"
import { ChangeEvent, ReactElement } from "react"
import { Control, Controller } from "react-hook-form"

import { useScreenSize } from "../../../hooks/useScreenSize"
import { INPUT_HELP_TOOLTIP_SPACING } from "../../../themes/defaultThemeParams"
import { parseReactHookFormRadioString } from "../../../utils/general/generalUtils"
import HelpIcon from "../../helper/helpIcon"

type TRadioOptions = {
    [key: string]: string | number | boolean
}

interface IValidatedRadioGroupProps {
    registerName: string
    label: string
    control: Control
    radioOptions: TRadioOptions
    required: boolean
    customRules?: any // todo
    tooltip?: null | string
    disabled?: boolean
}

/**
 * Radio group with designed to be controlled by react-hook-form. Must be used
 * in a component with a useForm hook from react-hook-form. Register name
 * and control are exposed from useForm.
 *
 * Options are in the format:
 * {
 *    "Label of value": "value",
 *    "Label of value 2": "value2",
 * }
 * The value is the value that will be submitted to form.
 *
 * Note: `RadioGroup` serializes the value to a string. If the value is a boolean,
 * the component will convert the string to a boolean. The field is considered
 * empty if the value is null or undefined.
 */
const ValidatedRadioGroup = ({
    registerName,
    label,
    control,
    radioOptions,
    required,
    customRules,
    tooltip,
    disabled,
}: IValidatedRadioGroupProps): ReactElement => {
    const requiredRule = {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        validate: (x: any) => {
            if (x === null || x === undefined) {
                return "This field is required"
            }
            return true
        },
    }
    const { isMob } = useScreenSize()
    const rules = required ? { ...requiredRule, ...customRules } : customRules
    return (
        <Stack
            direction="row"
            spacing={INPUT_HELP_TOOLTIP_SPACING}
            width="100%"
        >
            <FormControl>
                <Controller
                    rules={rules}
                    control={control}
                    name={registerName}
                    defaultValue={null}
                    render={({
                        field: { onChange, ...field },
                        fieldState: { error },
                    }) => (
                        <>
                            <FormLabel error={!!error} required={required}>
                                {label}
                            </FormLabel>
                            <RadioGroup
                                {...field}
                                onChange={(
                                    e: ChangeEvent<HTMLInputElement>
                                ) => {
                                    onChange(
                                        parseReactHookFormRadioString(
                                            e.target.value
                                        )
                                    )
                                }}
                            >
                                {Object.entries(radioOptions).map(
                                    ([labelOfValue, value]) => (
                                        <FormControlLabel
                                            key={String(value)}
                                            value={value}
                                            control={
                                                <Radio disabled={disabled} />
                                            }
                                            label={labelOfValue}
                                        />
                                    )
                                )}
                            </RadioGroup>
                            {!!error && (
                                <FormHelperText error={!!error}>
                                    {error?.message}
                                </FormHelperText>
                            )}
                        </>
                    )}
                />
            </FormControl>
            {tooltip && !isMob && <HelpIcon tooltip={tooltip} />}
        </Stack>
    )
}

ValidatedRadioGroup.defaultProps = {
    customRules: {},
    tooltip: null,
    disabled: false,
}

export default ValidatedRadioGroup
